// Post-parse sanity checks for uploaded documents (Ferrari POD/HAWB
// forms, Malca dispatcher emails). The parser reports what it saw;
// these checks flag uploads that don't look like the document the app
// expects, or whose numbers disagree with each other — the signatures
// of a wrong file or a misread scan. Warnings are advisory (never
// block): the owner sees an alert on the card / form and decides.

const norm = (s) => String(s || "").toUpperCase().replace(/[\s-]/g, "");

// A Ferrari Order/Tracking # is letters+digits (NYW600239567,
// MIW600021947, 26NY900ADXF all pass). We only flag values that are
// clearly NOT one — too short, or carrying characters that never
// appear in one (an OCR artifact) — so format drift between Ferrari
// offices doesn't cry wolf.
function looksLikeTracking(t) {
  const v = norm(t);
  return v.length >= 8 && /^[A-Z0-9]+$/.test(v);
}

/**
 * @param {Object} p
 *   p.documentShape  — parser's classification (extracted.documentShape)
 *   p.trackingNumber — the form's Order/Tracking #
 *   p.packages       — [{ hawbNumber }] parsed package rows
 *   p.stop           — the built local stop (null when unreadable)
 * @returns {string[]} human-readable warnings; empty = form checks out.
 */
export function ferrariParseWarnings(p = {}) {
  const warnings = [];
  const shape = p.documentShape || null;
  const isFerrariShape = shape === "ferrari_waybill" || shape === "ferrari_export_letter";
  if (!isFerrariShape) {
    warnings.push(
      shape
        ? `This doesn't look like a Ferrari form — it parsed as "${shape.replace(/_/g, " ")}". Double-check the right image was uploaded.`
        : "This doesn't look like a Ferrari form — no recognizable layout was found. Double-check the right image was uploaded."
    );
    // Number checks are meaningless on a non-Ferrari document.
    return warnings;
  }

  const tracking = norm(p.trackingNumber);
  if (!tracking) {
    warnings.push("No Order / Tracking # was read off the form — the job would be created without a number.");
  } else if (!looksLikeTracking(p.trackingNumber)) {
    warnings.push(`The Order / Tracking # read as "${String(p.trackingNumber).trim()}", which doesn't look like a Ferrari number — check the form.`);
  }

  // Cross-field agreement: on a Ferrari form every package row carries
  // the same Order/Tracking # printed at the top. A package number that
  // disagrees means the model misread one of the two.
  if (tracking) {
    for (const pkg of p.packages || []) {
      const hawb = norm(pkg?.hawbNumber);
      if (hawb && hawb !== tracking) {
        warnings.push(`Package number "${String(pkg.hawbNumber).trim()}" doesn't match the form's Tracking # "${String(p.trackingNumber).trim()}" — one of them was misread.`);
      }
    }
  }

  if (!p.stop) {
    warnings.push("No readable pickup/consignee block — the job would have no local stop.");
  }

  return warnings;
}

// Malca job numbers: MAKY26-1901 — "MA" + 2-letter office + 2-digit
// year, hyphen, 2–5 digit sequence, optional A/B guard-split suffix.
const MALCA_JOBNUM_RE = /^MA[A-Z]{2}\d{2}-\d{2,5}(-?[AB])?$/i;

/**
 * Sanity checks for a parsed Malca email.
 * @param {Object} p
 *   p.jobNumber — single-job parse's job number ("" when the email had none)
 *   p.packages  — [{ hawbNumber }] parsed receipt rows (single-job mode)
 *   p.bulkRows  — bulk-table rows [{ jobNumber, scheduledDate }]; when
 *                 present, the single-job fields are ignored.
 * @returns {string[]} advisory warnings; empty = parse checks out.
 */
export function malcaParseWarnings(p = {}) {
  const warnings = [];

  // ── Bulk-table mode: per-row + cross-row checks ──
  if (Array.isArray(p.bulkRows) && p.bulkRows.length > 0) {
    const seen = new Map(); // normalized job# → first row index
    p.bulkRows.forEach((row, idx) => {
      const n = idx + 1;
      const num = String(row.jobNumber || "").trim();
      if (!num) {
        warnings.push(`Row ${n} has no job number — Malca requires one on every job.`);
        return;
      }
      if (!MALCA_JOBNUM_RE.test(num)) {
        warnings.push(`Row ${n}'s job number "${num}" doesn't look like a Malca number (MAKY26-1234) — check the table.`);
      }
      const key = norm(num);
      if (seen.has(key)) {
        warnings.push(`Job number ${num} appears on rows ${seen.get(key)} and ${n} — the table may have been misread.`);
      } else {
        seen.set(key, n);
      }
      if (!row.scheduledDate) {
        warnings.push(`Row ${n} (${num}) has no date — it would be created unscheduled.`);
      }
    });
    return warnings;
  }

  // ── Single-job mode ──
  const num = String(p.jobNumber || "").trim();
  // Missing number is handled (and blocked) at save for Malca — only a
  // present-but-odd number is worth an early alert here.
  if (num && !MALCA_JOBNUM_RE.test(num)) {
    warnings.push(`Job number "${num}" doesn't look like a Malca number (MAKY26-1234) — check the email.`);
  }
  // The same receipt number on two package rows means a misread table.
  const seenPkg = new Map();
  for (const pkg of p.packages || []) {
    const hawb = norm(pkg?.hawbNumber);
    if (!hawb) continue; // blank rows are legit ("0" / unlisted receipts)
    if (seenPkg.has(hawb)) {
      warnings.push(`Receipt number ${String(pkg.hawbNumber).trim()} appears on more than one package row — the table may have been misread.`);
      break; // one alert is enough; the rows are right there to review
    }
    seenPkg.set(hawb, true);
  }
  return warnings;
}

// A Ferrari job's date is normally within a few weeks of the upload.
// Outside this window it's more likely a misread (a handwritten "26"
// read as "28") than a real date.
const MAX_DAYS_AHEAD = 60;
const MAX_DAYS_BACK = 120;

const ymdToDayNum = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000 : null;
};
const inWindow = (days) => days <= MAX_DAYS_AHEAD && days >= -MAX_DAYS_BACK;

/**
 * Flags a parsed job date that's implausibly far from today.
 * @param {string} ymd   the parsed date (YYYY-MM-DD)
 * @param {string} today today's date (YYYY-MM-DD)
 * @returns {null | { message: string, suggestedDate: string|null }}
 *   suggestedDate is the same month/day in today's year, offered only
 *   when that date would itself look normal (i.e. only the year is off).
 */
export function scheduledDateWarning(ymd, today) {
  const day = ymdToDayNum(ymd);
  const now = ymdToDayNum(today);
  if (day === null || now === null) return null;
  const diff = day - now;
  if (inWindow(diff)) return null;

  const message = diff > 0
    ? `The date read as ${ymd}, ${diff} days in the future — check the year on the form.`
    : `The date read as ${ymd}, ${-diff} days ago — check the year on the form.`;

  let suggestedDate = `${today.slice(0, 4)}${ymd.slice(4)}`;
  const sDay = ymdToDayNum(suggestedDate);
  if (suggestedDate === ymd || sDay === null || !inWindow(sDay - now)) suggestedDate = null;
  return { message, suggestedDate };
}
